/**
 * apps/web/src/routes/welcome.tsx
 *
 * First-run onboarding — shown once when a brand-new workspace has zero projects.
 * The root layout redirects here automatically (see __root.tsx patch below).
 *
 * Flow:
 *   Step 0 — Splash / workspace confirmation
 *   Step 1 — Name your first project
 *   Step 2 — Name your first phase
 *   Step 3 — Launching (API call + redirect to /projects/:id)
 *
 * After the user completes onboarding, localStorage key "ft_onboarded" is set
 * so the root layout never redirects here again (even with 0 projects while loading).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@forgetrack/auth';
import { ENGINE_COLORS as C } from '../components/engine/tokens';
import { useProjectMutations, usePhaseMutations } from '../lib/api/projects';

// ─── Route ──────────────────────────────────────────────────────────────────
export const Route = createFileRoute('/welcome')({
  component: WelcomePage,
});

// ─── Types ───────────────────────────────────────────────────────────────────
type Step = 0 | 1 | 2 | 3;

const EASE = [0.22, 1, 0.36, 1] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getWorkspaceName(): string {
  // subdomain is the workspace name — extract from current hostname
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const sub = host.split('.')[0];
  if (!sub || sub === 'localhost' || sub === 'app' || sub === 'www') return 'your workspace';
  // Prettify: "acme-rd" → "Acme Rd"
  return sub
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getUserFirstName(displayName: string | null | undefined): string {
  if (!displayName) return 'there';
  return displayName.split(' ')[0];
}

// ─── Main component ──────────────────────────────────────────────────────────
function WelcomePage() {
  const navigate = useNavigate();
  const { account } = useAuth();
  const projectMutations = useProjectMutations();
  const phaseMutations = usePhaseMutations();

  const [step, setStep] = useState<Step>(0);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [phaseName, setPhaseName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);

  const projectInputRef = useRef<HTMLInputElement>(null);
  const phaseInputRef = useRef<HTMLInputElement>(null);

  const workspaceName = getWorkspaceName();
  const firstName = getUserFirstName(account?.name);

  // Focus inputs on step change
  useEffect(() => {
    if (step === 1) setTimeout(() => projectInputRef.current?.focus(), 400);
    if (step === 2) setTimeout(() => phaseInputRef.current?.focus(), 400);
  }, [step]);

  // Step 1 → Step 2: create project
  const handleProjectNext = useCallback(async () => {
    if (!projectName.trim()) return;
    setError(null);

    const project = await projectMutations.create({
      name: projectName.trim(),
      description: projectDesc.trim() || undefined,
      status: 'Active',
      startDate: new Date().toISOString().split('T')[0],
    });

    if (!project) {
      setError(projectMutations.error?.message ?? 'Failed to create project');
      return;
    }

    setCreatedProjectId(project.id);
    setStep(2);
  }, [projectName, projectDesc, projectMutations]);

  // Step 2 → Step 3: create phase + navigate
  const handlePhaseNext = useCallback(async () => {
    if (!phaseName.trim() || !createdProjectId) return;
    setError(null);
    setStep(3);

    const phase = await phaseMutations.create({
      projectId: createdProjectId,
      name: phaseName.trim(),
      orderIndex: 1,
    });

    if (!phase) {
      setError('Failed to create phase');
      setStep(2);
      return;
    }

    // Mark as onboarded so root layout never redirects here again
    localStorage.setItem('ft_onboarded', '1');

    // Small delay to let the launch animation breathe
    await new Promise((r) => setTimeout(r, 1800));
    navigate({ to: '/projects/$projectId', params: { projectId: String(createdProjectId) } });
  }, [phaseName, createdProjectId, phaseMutations, navigate]);

  // Skip onboarding (go to dashboard as-is)
  const handleSkip = useCallback(() => {
    localStorage.setItem('ft_onboarded', '1');
    navigate({ to: '/dashboard', replace: true });
  }, [navigate]);

  return (
    <>
      <style>{WELCOME_CSS}</style>
      <div className="wlc-root">
        {/* Animated background layers */}
        <div className="wlc-bg-orb wlc-bg-orb--a" />
        <div className="wlc-bg-orb wlc-bg-orb--b" />
        <div className="wlc-bg-orb wlc-bg-orb--c" />
        <div className="wlc-grid" />

        {/* Particle field */}
        <ParticleField />

        {/* Brand mark */}
        <motion.div
          className="wlc-brand"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <span className="wlc-brand__logo">⚡</span>
          <span className="wlc-brand__name">ForgeTrack</span>
          <span className="wlc-brand__tier">FREE TIER</span>
        </motion.div>

        {/* Step progress */}
        <AnimatePresence mode="wait">
          {step < 3 && (
            <motion.div
              key="progress"
              className="wlc-progress"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {([0, 1, 2] as Step[]).map((s) => (
                <div
                  key={s}
                  className={`wlc-progress__dot ${step >= s ? 'wlc-progress__dot--active' : ''}`}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cards */}
        <div className="wlc-stage">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <SplashCard
                key="splash"
                firstName={firstName}
                workspaceName={workspaceName}
                onNext={() => setStep(1)}
                onSkip={handleSkip}
              />
            )}
            {step === 1 && (
              <ProjectCard
                key="project"
                inputRef={projectInputRef}
                projectName={projectName}
                projectDesc={projectDesc}
                onNameChange={setProjectName}
                onDescChange={setProjectDesc}
                onNext={handleProjectNext}
                loading={projectMutations.loading}
                error={error}
              />
            )}
            {step === 2 && (
              <PhaseCard
                key="phase"
                inputRef={phaseInputRef}
                projectName={projectName}
                phaseName={phaseName}
                onPhaseChange={setPhaseName}
                onNext={handlePhaseNext}
                onBack={() => setStep(1)}
                loading={phaseMutations.loading}
                error={error}
              />
            )}
            {step === 3 && (
              <LaunchCard
                key="launch"
                projectName={projectName}
                phaseName={phaseName}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Free tier callout strip */}
        <AnimatePresence>
          {step < 3 && (
            <motion.div
              className="wlc-tier-strip"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ delay: 0.8 }}
            >
              <span className="wlc-tier-strip__dot" />
              <span>Free tier · 1 project · 50 work items · 10 AI estimations/mo</span>
              <span className="wlc-tier-strip__sep">·</span>
              <a href="/settings/billing" className="wlc-tier-strip__upgrade">Upgrade anytime →</a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

// ─── Step cards ───────────────────────────────────────────────────────────────

function SplashCard({
  firstName,
  workspaceName,
  onNext,
  onSkip,
}: {
  firstName: string;
  workspaceName: string;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <motion.div
      className="wlc-card"
      initial={{ opacity: 0, y: 32, filter: 'blur(12px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: -24, filter: 'blur(8px)', scale: 0.97 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Glowing icon */}
      <motion.div
        className="wlc-card__icon"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 18 }}
      >
        <span>⚡</span>
        <div className="wlc-card__icon-ring" />
      </motion.div>

      <motion.div
        className="wlc-card__eyebrow"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        WORKSPACE READY
      </motion.div>

      <motion.h1
        className="wlc-card__title"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        Welcome, {firstName}
      </motion.h1>

      <motion.p
        className="wlc-card__subtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
      >
        <strong className="wlc-workspace-name">{workspaceName}</strong> is live.
        Let's set up your first R&D project so ForgeTrack can start tracking effort,
        decisions, and AI-powered estimations.
      </motion.p>

      {/* Feature pills */}
      <motion.div
        className="wlc-features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
      >
        {[
          { icon: '🔮', label: 'AI estimation', color: C.violet },
          { icon: '📋', label: 'IRS R&D docs', color: C.cyan },
          { icon: '📊', label: 'Investor reports', color: C.amber },
        ].map((f) => (
          <div key={f.label} className="wlc-feature-pill" style={{ '--pill-color': f.color } as React.CSSProperties}>
            <span>{f.icon}</span>
            <span>{f.label}</span>
          </div>
        ))}
      </motion.div>

      <motion.div
        className="wlc-actions"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
      >
        <button className="wlc-btn wlc-btn--primary" onClick={onNext}>
          <span>Set up my workspace</span>
          <span className="wlc-btn__arrow">→</span>
        </button>
        <button className="wlc-btn wlc-btn--ghost" onClick={onSkip}>
          Skip for now
        </button>
      </motion.div>
    </motion.div>
  );
}

function ProjectCard({
  inputRef,
  projectName,
  projectDesc,
  onNameChange,
  onDescChange,
  onNext,
  loading,
  error,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  projectName: string;
  projectDesc: string;
  onNameChange: (v: string) => void;
  onDescChange: (v: string) => void;
  onNext: () => void;
  loading: boolean;
  error: string | null;
}) {
  return (
    <motion.div
      className="wlc-card"
      initial={{ opacity: 0, x: 40, filter: 'blur(8px)' }}
      animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, x: -40, filter: 'blur(8px)', scale: 0.97 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="wlc-card__step-badge"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 }}
      >
        STEP 1 OF 2
      </motion.div>

      <motion.h1
        className="wlc-card__title"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        Name your first project
      </motion.h1>

      <motion.p
        className="wlc-card__subtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.28 }}
      >
        A project is the top-level unit — your product, product line, or research area.
        You can add more projects later.
      </motion.p>

      <motion.div
        className="wlc-fields"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <div className="wlc-field">
          <label className="wlc-field__label" htmlFor="project-name">PROJECT NAME</label>
          <input
            id="project-name"
            ref={inputRef}
            className="wlc-field__input"
            type="text"
            value={projectName}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && projectName.trim() && onNext()}
            placeholder="e.g. LoanForge, Acme API, R&D Platform"
            maxLength={100}
            disabled={loading}
            autoComplete="off"
          />
          <div className="wlc-field__hint">
            This will be the name displayed in all reports and dashboards.
          </div>
        </div>

        <div className="wlc-field">
          <label className="wlc-field__label" htmlFor="project-desc">
            DESCRIPTION <span className="wlc-field__optional">OPTIONAL</span>
          </label>
          <textarea
            id="project-desc"
            className="wlc-field__textarea"
            value={projectDesc}
            onChange={(e) => onDescChange(e.target.value)}
            placeholder="What are you building? (helps with AI estimations)"
            maxLength={500}
            rows={3}
            disabled={loading}
          />
        </div>
      </motion.div>

      {error && (
        <motion.div
          className="wlc-error"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.div>
      )}

      <motion.div
        className="wlc-actions"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
      >
        <button
          className="wlc-btn wlc-btn--primary"
          onClick={onNext}
          disabled={!projectName.trim() || loading}
        >
          {loading ? (
            <>
              <span className="wlc-spinner" />
              <span>Creating…</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <span className="wlc-btn__arrow">→</span>
            </>
          )}
        </button>
      </motion.div>
    </motion.div>
  );
}

function PhaseCard({
  inputRef,
  projectName,
  phaseName,
  onPhaseChange,
  onNext,
  onBack,
  loading,
  error,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  projectName: string;
  phaseName: string;
  onPhaseChange: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}) {
  const PHASE_SUGGESTIONS = ['MVP', 'Alpha', 'Beta', 'Foundation', 'Sprint 1', 'Phase 1', 'Core Engine'];

  return (
    <motion.div
      className="wlc-card"
      initial={{ opacity: 0, x: 40, filter: 'blur(8px)' }}
      animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, x: -40, filter: 'blur(8px)', scale: 0.97 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="wlc-card__step-badge"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 }}
      >
        STEP 2 OF 2
      </motion.div>

      <motion.div
        className="wlc-card__context"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <span className="wlc-card__context-icon">📐</span>
        <span className="wlc-card__context-name">{projectName}</span>
      </motion.div>

      <motion.h1
        className="wlc-card__title"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        Add your first phase
      </motion.h1>

      <motion.p
        className="wlc-card__subtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.32 }}
      >
        Phases break your project into meaningful development stages. Work items, sessions,
        and AI estimations live inside phases.
      </motion.p>

      <motion.div
        className="wlc-fields"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.38 }}
      >
        <div className="wlc-field">
          <label className="wlc-field__label" htmlFor="phase-name">PHASE NAME</label>
          <input
            id="phase-name"
            ref={inputRef}
            className="wlc-field__input"
            type="text"
            value={phaseName}
            onChange={(e) => onPhaseChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && phaseName.trim() && onNext()}
            placeholder="e.g. MVP, Foundation, Sprint 1"
            maxLength={80}
            disabled={loading}
            autoComplete="off"
          />
        </div>

        {/* Quick-pick suggestions */}
        <motion.div
          className="wlc-suggestions"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.48 }}
        >
          {PHASE_SUGGESTIONS.map((s, i) => (
            <motion.button
              key={s}
              className={`wlc-suggestion ${phaseName === s ? 'wlc-suggestion--active' : ''}`}
              onClick={() => onPhaseChange(s)}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.04 }}
              disabled={loading}
            >
              {s}
            </motion.button>
          ))}
        </motion.div>
      </motion.div>

      {error && (
        <motion.div
          className="wlc-error"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.div>
      )}

      <motion.div
        className="wlc-actions"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <button
          className="wlc-btn wlc-btn--primary"
          onClick={onNext}
          disabled={!phaseName.trim() || loading}
        >
          {loading ? (
            <>
              <span className="wlc-spinner" />
              <span>Creating…</span>
            </>
          ) : (
            <>
              <span>Launch workspace</span>
              <span className="wlc-btn__arrow">⚡</span>
            </>
          )}
        </button>
        <button className="wlc-btn wlc-btn--ghost" onClick={onBack} disabled={loading}>
          ← Back
        </button>
      </motion.div>
    </motion.div>
  );
}

function LaunchCard({
  projectName,
  phaseName,
}: {
  projectName: string;
  phaseName: string;
}) {
  return (
    <motion.div
      className="wlc-card wlc-card--launch"
      initial={{ opacity: 0, scale: 0.9, filter: 'blur(16px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Expanding ring animation */}
      <div className="wlc-launch-rings" aria-hidden>
        <div className="wlc-launch-ring wlc-launch-ring--1" />
        <div className="wlc-launch-ring wlc-launch-ring--2" />
        <div className="wlc-launch-ring wlc-launch-ring--3" />
      </div>

      <motion.div
        className="wlc-card__icon wlc-card__icon--launch"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 14 }}
      >
        <span>⚡</span>
      </motion.div>

      <motion.h1
        className="wlc-card__title"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        Workspace is live
      </motion.h1>

      <motion.div
        className="wlc-launch-summary"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="wlc-launch-row">
          <span className="wlc-launch-row__label">PROJECT</span>
          <span className="wlc-launch-row__value">{projectName}</span>
        </div>
        <div className="wlc-launch-row">
          <span className="wlc-launch-row__label">PHASE</span>
          <span className="wlc-launch-row__value">{phaseName}</span>
        </div>
        <div className="wlc-launch-row">
          <span className="wlc-launch-row__label">TIER</span>
          <span className="wlc-launch-row__value wlc-launch-row__value--neon">Free</span>
        </div>
      </motion.div>

      <motion.p
        className="wlc-card__subtitle"
        style={{ textAlign: 'center', marginTop: 8 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
      >
        Taking you to your project…
      </motion.p>

      <motion.div
        className="wlc-launch-bar"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <motion.div
          className="wlc-launch-bar__fill"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ delay: 0.65, duration: 1.4, ease: 'easeInOut' }}
        />
      </motion.div>
    </motion.div>
  );
}

// ─── Particle field ───────────────────────────────────────────────────────────
function ParticleField() {
  // Static seed — doesn't change on re-render
  const particles = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    x: (i * 4.17 + 8) % 100,
    y: (i * 7.83 + 12) % 100,
    size: 1 + (i % 3),
    delay: (i * 0.41) % 4,
    dur: 4 + (i % 6),
  }));

  return (
    <div className="wlc-particles" aria-hidden>
      {particles.map((p) => (
        <div
          key={p.id}
          className="wlc-particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const WELCOME_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

.wlc-root {
  --wlc-bg:       #070b18;
  --wlc-surface:  rgba(10, 16, 36, 0.82);
  --wlc-border:   rgba(100, 130, 220, 0.18);
  --wlc-text:     #e8ecf4;
  --wlc-muted:    rgba(232,236,244,0.62);
  --wlc-dim:      rgba(232,236,244,0.38);
  --wlc-neon:     #34d399;
  --wlc-cyan:     #22d3ee;
  --wlc-violet:   #a78bfa;
  --wlc-amber:    #fbbf24;
  --wlc-red:      #f87171;

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--wlc-bg);
  color: var(--wlc-text);
  font-family: 'Outfit', system-ui, sans-serif;
  overflow: hidden;
}

/* ── Background layers ── */
.wlc-bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: wlc-drift ease-in-out infinite;
}
.wlc-bg-orb--a {
  width: 600px; height: 600px;
  top: -20%; left: -10%;
  background: radial-gradient(circle, rgba(52,211,153,0.12), transparent 70%);
  animation-duration: 22s;
}
.wlc-bg-orb--b {
  width: 500px; height: 500px;
  bottom: -15%; right: -8%;
  background: radial-gradient(circle, rgba(167,139,250,0.14), transparent 70%);
  animation-duration: 28s;
  animation-delay: -8s;
}
.wlc-bg-orb--c {
  width: 400px; height: 400px;
  top: 40%; left: 55%;
  background: radial-gradient(circle, rgba(34,211,238,0.08), transparent 70%);
  animation-duration: 18s;
  animation-delay: -4s;
}
@keyframes wlc-drift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(2%, -2%) scale(1.04); }
  66%       { transform: translate(-1%, 3%) scale(0.97); }
}

.wlc-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(100,130,220,0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100,130,220,0.045) 1px, transparent 1px);
  background-size: 52px 52px;
  mask-image: radial-gradient(ellipse 75% 65% at 50% 50%, black 40%, transparent 100%);
  pointer-events: none;
}

/* ── Particles ── */
.wlc-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.wlc-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(52,211,153,0.55);
  box-shadow: 0 0 4px rgba(52,211,153,0.4);
  animation: wlc-particle-float ease-in-out infinite;
}
@keyframes wlc-particle-float {
  0%, 100% { opacity: 0; transform: translateY(0px); }
  20%       { opacity: 0.7; }
  80%       { opacity: 0.5; }
  100%      { opacity: 0; transform: translateY(-40px); }
}

/* ── Brand ── */
.wlc-brand {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
}
.wlc-brand__logo {
  font-size: 22px;
  filter: drop-shadow(0 0 8px rgba(52,211,153,0.7));
}
.wlc-brand__name {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  font-size: 17px;
  color: var(--wlc-text);
  letter-spacing: -0.02em;
}
.wlc-brand__tier {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: var(--wlc-neon);
  background: rgba(52,211,153,0.1);
  border: 1px solid rgba(52,211,153,0.25);
  padding: 2px 7px;
  border-radius: 4px;
}

/* ── Progress dots ── */
.wlc-progress {
  position: absolute;
  top: 28px;
  right: 28px;
  display: flex;
  gap: 7px;
  align-items: center;
}
.wlc-progress__dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--wlc-border);
  transition: background 0.35s ease, box-shadow 0.35s ease, transform 0.35s ease;
}
.wlc-progress__dot--active {
  background: var(--wlc-neon);
  box-shadow: 0 0 6px rgba(52,211,153,0.6);
  transform: scale(1.3);
}

/* ── Stage ── */
.wlc-stage {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 480px;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Card ── */
.wlc-card {
  width: 100%;
  background: var(--wlc-surface);
  border: 1px solid var(--wlc-border);
  border-radius: 20px;
  padding: 36px 36px 32px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.04) inset,
    0 32px 64px -12px rgba(0,0,0,0.55),
    0 0 48px -16px rgba(52,211,153,0.08);
  position: relative;
  overflow: hidden;
}
/* inner scanline */
.wlc-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(255,255,255,0.012) 2px,
    rgba(255,255,255,0.012) 4px
  );
  pointer-events: none;
  border-radius: inherit;
}
/* neon accent top edge */
.wlc-card::after {
  content: '';
  position: absolute;
  top: 0; left: 24px; right: 24px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(52,211,153,0.4), transparent);
  pointer-events: none;
}

.wlc-card--launch {
  text-align: center;
  padding-bottom: 36px;
}
.wlc-card--launch::after {
  background: linear-gradient(90deg, transparent, rgba(52,211,153,0.6), transparent);
}

/* ── Card icon ── */
.wlc-card__icon {
  width: 64px; height: 64px;
  border-radius: 18px;
  background: rgba(52,211,153,0.1);
  border: 1px solid rgba(52,211,153,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 20px;
  position: relative;
  box-shadow: 0 0 20px rgba(52,211,153,0.15);
}
.wlc-card__icon--launch {
  width: 80px; height: 80px;
  font-size: 36px;
  background: rgba(52,211,153,0.15);
  border-color: rgba(52,211,153,0.4);
  box-shadow: 0 0 32px rgba(52,211,153,0.25), 0 0 0 8px rgba(52,211,153,0.05);
  margin-bottom: 24px;
  animation: wlc-icon-pulse 2s ease-in-out infinite;
}
@keyframes wlc-icon-pulse {
  0%, 100% { box-shadow: 0 0 32px rgba(52,211,153,0.25), 0 0 0 8px rgba(52,211,153,0.05); }
  50%       { box-shadow: 0 0 44px rgba(52,211,153,0.4), 0 0 0 14px rgba(52,211,153,0.08); }
}

.wlc-card__icon-ring {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  border: 1px solid rgba(52,211,153,0.2);
  animation: wlc-ring-spin 12s linear infinite;
}
@keyframes wlc-ring-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ── Typography ── */
.wlc-card__eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: var(--wlc-neon);
  margin-bottom: 10px;
  text-align: center;
}
.wlc-card__step-badge {
  display: inline-block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: var(--wlc-cyan);
  background: rgba(34,211,238,0.1);
  border: 1px solid rgba(34,211,238,0.2);
  padding: 3px 9px;
  border-radius: 5px;
  margin-bottom: 16px;
}
.wlc-card__context {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}
.wlc-card__context-icon { font-size: 14px; }
.wlc-card__context-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--wlc-cyan);
  font-family: 'JetBrains Mono', monospace;
}

.wlc-card__title {
  font-family: 'Outfit', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: var(--wlc-text);
  letter-spacing: -0.025em;
  margin: 0 0 12px;
  line-height: 1.2;
}
.wlc-card__subtitle {
  font-size: 14px;
  color: var(--wlc-muted);
  line-height: 1.65;
  margin: 0 0 24px;
}

/* ── Workspace name highlight ── */
.wlc-workspace-name {
  color: var(--wlc-neon);
  font-weight: 700;
}

/* ── Feature pills ── */
.wlc-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
}
.wlc-feature-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: var(--pill-color, var(--wlc-neon));
  background: color-mix(in srgb, var(--pill-color, var(--wlc-neon)) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--pill-color, var(--wlc-neon)) 25%, transparent);
  padding: 4px 10px;
  border-radius: 20px;
}

/* ── Fields ── */
.wlc-fields {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 24px;
}
.wlc-field { display: flex; flex-direction: column; gap: 6px; }
.wlc-field__label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: var(--wlc-dim);
  display: flex;
  gap: 8px;
  align-items: center;
}
.wlc-field__optional {
  font-size: 8.5px;
  color: rgba(232,236,244,0.25);
  letter-spacing: 0.08em;
}
.wlc-field__input,
.wlc-field__textarea {
  background: rgba(0,0,0,0.35);
  border: 1px solid var(--wlc-border);
  border-radius: 10px;
  padding: 11px 14px;
  font-family: 'Outfit', sans-serif;
  font-size: 15px;
  color: var(--wlc-text);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  resize: none;
  width: 100%;
  box-sizing: border-box;
}
.wlc-field__input:focus,
.wlc-field__textarea:focus {
  border-color: rgba(52,211,153,0.45);
  box-shadow: 0 0 0 3px rgba(52,211,153,0.08);
}
.wlc-field__input::placeholder,
.wlc-field__textarea::placeholder {
  color: rgba(232,236,244,0.22);
}
.wlc-field__input:disabled,
.wlc-field__textarea:disabled { opacity: 0.5; cursor: not-allowed; }
.wlc-field__hint {
  font-size: 11.5px;
  color: var(--wlc-dim);
  line-height: 1.5;
}

/* ── Suggestions ── */
.wlc-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.wlc-suggestion {
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--wlc-muted);
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--wlc-border);
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.wlc-suggestion:hover {
  background: rgba(52,211,153,0.1);
  border-color: rgba(52,211,153,0.3);
  color: var(--wlc-neon);
}
.wlc-suggestion--active {
  background: rgba(52,211,153,0.14);
  border-color: rgba(52,211,153,0.45);
  color: var(--wlc-neon);
}
.wlc-suggestion:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Error ── */
.wlc-error {
  font-size: 12px;
  color: var(--wlc-red);
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.2);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
}

/* ── Actions ── */
.wlc-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Buttons ── */
.wlc-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px 20px;
  border-radius: 12px;
  font-family: 'Outfit', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  border: none;
  outline: none;
  text-decoration: none;
}
.wlc-btn--primary {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: #060e18;
  box-shadow: 0 0 20px rgba(52,211,153,0.25), 0 4px 12px rgba(0,0,0,0.3);
}
.wlc-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 0 28px rgba(52,211,153,0.4), 0 6px 16px rgba(0,0,0,0.35);
}
.wlc-btn--primary:active:not(:disabled) { transform: translateY(0); }
.wlc-btn--primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}
.wlc-btn--ghost {
  background: transparent;
  color: var(--wlc-dim);
  border: 1px solid rgba(255,255,255,0.08);
}
.wlc-btn--ghost:hover:not(:disabled) {
  color: var(--wlc-muted);
  border-color: rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.04);
}
.wlc-btn__arrow {
  font-size: 16px;
  transition: transform 0.2s ease;
}
.wlc-btn:hover .wlc-btn__arrow { transform: translateX(3px); }

/* ── Spinner ── */
.wlc-spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(6,14,24,0.3);
  border-top-color: rgba(6,14,24,0.9);
  border-radius: 50%;
  animation: wlc-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes wlc-spin {
  to { transform: rotate(360deg); }
}

/* ── Launch card specifics ── */
.wlc-launch-rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: inherit;
}
.wlc-launch-ring {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%) scale(0);
  border-radius: 50%;
  border: 1px solid rgba(52,211,153,0.25);
  animation: wlc-ring-expand 2.4s ease-out infinite;
}
.wlc-launch-ring--1 { width: 200px; height: 200px; animation-delay: 0s; }
.wlc-launch-ring--2 { width: 340px; height: 340px; animation-delay: 0.55s; }
.wlc-launch-ring--3 { width: 520px; height: 520px; animation-delay: 1.1s; }
@keyframes wlc-ring-expand {
  0%   { transform: translate(-50%, -50%) scale(0); opacity: 0.7; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
}

.wlc-launch-summary {
  background: rgba(0,0,0,0.3);
  border: 1px solid var(--wlc-border);
  border-radius: 12px;
  padding: 16px;
  margin: 0 auto 20px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.wlc-launch-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.wlc-launch-row__label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.12em;
  color: var(--wlc-dim);
}
.wlc-launch-row__value {
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--wlc-text);
}
.wlc-launch-row__value--neon {
  color: var(--wlc-neon);
  text-shadow: 0 0 8px rgba(52,211,153,0.5);
}

.wlc-launch-bar {
  width: 100%;
  height: 3px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 16px;
}
.wlc-launch-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #34d399, #22d3ee);
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(52,211,153,0.6);
}

/* ── Tier strip ── */
.wlc-tier-strip {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  color: var(--wlc-dim);
  white-space: nowrap;
  z-index: 10;
}
.wlc-tier-strip__dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: var(--wlc-neon);
  box-shadow: 0 0 5px var(--wlc-neon);
  flex-shrink: 0;
  animation: wlc-dot-pulse 2s ease-in-out infinite;
}
@keyframes wlc-dot-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}
.wlc-tier-strip__sep { color: rgba(232,236,244,0.2); }
.wlc-tier-strip__upgrade {
  color: var(--wlc-neon);
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.15s ease;
}
.wlc-tier-strip__upgrade:hover { opacity: 0.75; }

/* ── Responsive ── */
@media (max-width: 520px) {
  .wlc-card { padding: 28px 22px 24px; }
  .wlc-card__title { font-size: 22px; }
  .wlc-features { display: none; }
  .wlc-tier-strip { font-size: 10px; gap: 5px; padding: 0 16px; }
}
`;
